import { nextTick } from '@/js/utils/browser';
import { TOPIC_IDS } from '@/js/constants';
import { windowState } from '@/js/WindowState';

type MegaMenuInstance = {
  details: HTMLDetailsElement;
  summary: HTMLElement;
  content: HTMLElement;
  state: {
    isExpanded: boolean;
    isEffecting: boolean;
  };
  toggle: (e: MouseEvent) => void;
  expand: () => void;
  close: (option?: { noEffect: boolean }) => void;
  closeCallback: null | ((e: Event) => void);
};

export class MegaMenu {
  private el: HTMLDetailsElement[];
  private globalState: {
    disabled: boolean;
    activeMenu: null | MegaMenuInstance;
    mousePos: { x: number; y: number };
    lastExpandMousePos: { x: number; y: number };
  };
  private instances: MegaMenuInstance[];

  constructor(selector: string) {
    this.el = [...document.querySelectorAll(selector)] as HTMLDetailsElement[];
    this.globalState = {
      disabled: false,
      activeMenu: null,
      mousePos: { x: 0, y: 0 },
      lastExpandMousePos: { x: 0, y: 0 },
    };
    this.instances = [];

    if (!this.el.length) return;

    this.bind();
    this.initialize();
  }

  createInternalState() {
    return {
      isExpanded: false,
      isEffecting: false,
    };
  }

  bind() {
    this.onResize = this.onResize.bind(this);
  }

  initialize() {
    PubSub.subscribe(TOPIC_IDS.WINDOW_CHANGE_BREAKPOINT, this.onResize);
    this.onResize(null, windowState.state);
    this.instances = this.el.reduce<typeof this.instances>((acc, it) => {
      const toggler = it.querySelector('[data-toggler]') as HTMLElement | null;
      const togglerLink = toggler?.querySelector('a') as HTMLAnchorElement | null;

      if (!toggler || !togglerLink) {
        // toggler または togglerLink が null の場合はスキップ
        return acc;
      }

      const instance: MegaMenuInstance = {
        details: it,
        summary: togglerLink,
        content: it.querySelector('[data-content]') as HTMLElement,
        state: this.createInternalState(),
        toggle: async (e: MouseEvent) => {
          if (instance.state.isEffecting) return;
          if (this.globalState.disabled) return;

          const target = e.target as HTMLElement;

          const togglerChildElements: NodeListOf<HTMLElement> = toggler.querySelectorAll('*');
          // targetがtogglerChildElements, または自分自身の場合は, 処理をスキップ
          // if (togglerChildElements && Array.from(togglerChildElements).includes(target)) {
          //   return;
          // }

          // 開いている場合は close
          if (instance.state.isExpanded) {
            if (this.globalState.activeMenu === instance) {
              this.globalState.activeMenu = null;
            } else {
              await this.close(instance);
            }
          } else {
            // 他に active なメニューがあれば先に閉じる
            if (this.globalState.activeMenu && this.globalState.activeMenu !== instance) {
              await this.globalState.activeMenu.close({ noEffect: true });
              this.globalState.activeMenu = null;
            }
            await this.expand(instance);
            this.globalState.activeMenu = instance;
          }
        },
        expand: () => this.expand(instance),
        close: (option?: { noEffect: boolean }) => this.close(instance, option),
        closeCallback: null,
      };

      // 背景にhoverで閉じる
      const contentBg = instance.details.querySelector('.o-global-nav__content-bg') as HTMLElement;
      if (contentBg) {
        contentBg.addEventListener('mouseenter', async () => {
          if (
            instance.state.isExpanded &&
            !instance.state.isEffecting &&
            this.globalState.lastExpandMousePos.x !== this.globalState.mousePos.x &&
            this.globalState.lastExpandMousePos.y !== this.globalState.mousePos.y
          ) {
            await this.close(instance);
            if (this.globalState.activeMenu === instance) {
              this.globalState.activeMenu = null;
            }
          }
        });
      }

      // TODO: メニュー外(aタグと、hoverサポート用の要素を除く)hoverで閉じる処理を追加
      togglerLink.addEventListener('mouseover', instance.toggle);
      it.addEventListener('mouseleave', async () => {
        if (instance.state.isExpanded && !this.globalState.disabled) {
          await this.close(instance);
          if (this.globalState.activeMenu === instance) {
            this.globalState.activeMenu = null;
          }
        }
      });

      // for a11y
      toggler.addEventListener('focus', (e) => {
        const target = e.target as HTMLElement;
        const details = target.closest('details');
        if (!details) return;
        this.expand(instance);
      });

      // for a11y
      it.addEventListener('focusout', (e) => {
        const relatedTarget = e.relatedTarget as HTMLElement;
        if (!it.contains(relatedTarget)) {
          this.close(instance);
        }
      });

      acc.push(instance);
      return acc;
    }, []);

    window.addEventListener('mousemove', this.onMouseMove.bind(this));
  }

  onResize(_: any, state: any) {
    this.globalState.disabled = state.current.id === 'mobile';
  }

  onMouseMove(e: MouseEvent) {
    this.globalState.mousePos = {
      x: e.clientX,
      y: e.clientY,
    };
  }

  async expand(instance: MegaMenuInstance) {
    if (this.globalState.disabled) return;
    this.globalState.lastExpandMousePos = this.globalState.mousePos;

    document.body.classList.add('is-mega-menu-expanded');

    instance.details.open = true;
    instance.state.isExpanded = true;

    void instance.content.offsetWidth; // 初回読み込み時にアニメーションが効かないため, 強制的にリフロー

    await nextTick();
    instance.content.classList.add('is-expanded');
    instance.summary.classList.add('is-active');
  }
  async close(instance: MegaMenuInstance, { noEffect = false } = {}) {
    if (this.globalState.disabled) return;
    instance.state.isExpanded = false;

    if (noEffect) {
      instance.content.classList.remove('is-expanded');
      instance.summary.classList.remove('is-active');
      document.body.classList.remove('is-mega-menu-expanded');
      instance.details.open = false;
      return;
    }

    instance.state.isEffecting = true;
    instance.closeCallback = () => {
      if (instance.closeCallback) {
        instance.content.removeEventListener('transitionend', instance.closeCallback);
      }
      instance.details.open = false;
      document.body.classList.remove('is-mega-menu-expanded');
      instance.state.isEffecting = false;
      clearTimeout(fallbackTimer);
    };
    instance.content.addEventListener('transitionend', instance.closeCallback);
    instance.content.classList.remove('is-expanded');
    instance.summary.classList.remove('is-active');

    // ↓ これはイベント設計で対処した方がよいです
    // フォールバックタイマー：transitionend が発火しなかった場合にロックを解除
    const fallbackTimer = setTimeout(() => {
      if (instance.state.isEffecting) {
        instance.content.removeEventListener('transitionend', instance.closeCallback!);
        instance.details.open = false;
        document.body.classList.remove('is-mega-menu-expanded');
        instance.state.isEffecting = false;
      }
    }, 500); // 500ms でフォールバック
  }
}
